import React from 'react';
import type { SxProps } from '@mui/system';

import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import TruncateMarkup from 'react-truncate-markup';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Typography from 'src/components/ui/Typography';
import Button from 'src/components/ui/Button';
import Icon from 'src/components/ui/Icon';
import JobOfferCardMenu from 'src/components/job-offer/JobOfferCardMenu';

import formatDistanceToNow from 'src/utils/formatDistanceToNow';

import type { JobOffersQuery, JobOffer } from 'src/types/__generated__/graphql';

import useAppRouter from 'src/hooks/useAppRouter';
import { routePathCompany } from 'src/constants/router';
import { styledComponent } from 'src/utils/styled';
import { AnalyticsContextProvider } from 'src/hooks/useDynamicLogEvents';
import { EventParamKeys } from 'src/utils/analytics';

const paddedButton: SxProps = {
    paddingTop: '0.9rem',
    paddingBottom: '0.9rem',
    borderRadius: 0,
};

const StyledCard = styledComponent(Card)<{
    $addTopPaddingForCompanyPage: boolean;
}>`
    border-radius: 0.625rem;
    display: flex;
    flex-direction: column;

    .MuiCardHeader-root {
        cursor: pointer;
        padding-top: 0.1875rem;
        padding-bottom: 0rem;
        ${({ $addTopPaddingForCompanyPage }) => ($addTopPaddingForCompanyPage ? '' : 'padding-top: 1rem;')}
    }
    .MuiCardHeader-action {
        margin: 0;
    }
    .MuiCardContent-root {
        display: flex;
        flex-direction: column;
        padding-top: 0;
        padding-bottom: 0.375rem;
        flex: 1;
    }
    .MuiCardActions-root {
        padding: 0;
    }
`;

interface Props {
    jobOffer: ArrayElement<JobOffersQuery['jobOffers']>;
    displayEditMenu: boolean;
}

const JobOfferCard: React.FC<Props> = ({ jobOffer, displayEditMenu }) => {
    const { t, i18n } = useTranslation(['contacts', 'company']);
    const router = useAppRouter();

    const jobTypesLanguage = i18n.language === 'fr' || i18n.language === 'en' ? i18n.language : 'fr';

    const actions = (
        <Box display="flex" flexDirection="row">
            <AnalyticsContextProvider
                value={{
                    [EventParamKeys.ItemId]: jobOffer.id,
                    [EventParamKeys.ItemName]: jobOffer.title,
                }}
            >
                <JobOfferCardMenu jobOffer={jobOffer as JobOffer} displayEditMenu={displayEditMenu} />
            </AnalyticsContextProvider>
        </Box>
    );

    return (
        <StyledCard
            key={jobOffer.id}
            data-target="job-offer-card"
            data-target-id={`job-offer-card-${jobOffer.title.replace(/\s/g, '-').toLowerCase()}`}
            elevation={2}
            style={{ opacity: jobOffer.isHidden ? '0.5' : '1' }}
            $addTopPaddingForCompanyPage={displayEditMenu}
        >
            <CardHeader action={actions} />
            <CardContent id={`card-content-${jobOffer.id}`}>
                <Box display="flex" flexDirection="column" justifyContent="center" flex="1" minHeight="3.6rem">
                    <Typography
                        data-target="job-offer-card-text"
                        data-target-id="job-offer-card-title"
                        variant="h5"
                        align="center"
                        paragraph
                    >
                        <TruncateMarkup lines={2}>
                            <span>{jobOffer.title}</span>
                        </TruncateMarkup>
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    <Icon name="Map" size="1rem" />
                    <Typography
                        data-target="job-offer-card-text"
                        data-target-id="job-offer-card-city"
                        variant="body2"
                        margin="auto"
                    >
                        {jobOffer.city.name}
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" marginTop="1rem">
                    <Icon name="File" size="1rem" />
                    <Typography
                        data-target="job-offer-card-text"
                        data-target-id="job-offer-card-type"
                        variant="body2"
                        margin="auto"
                    >
                        {jobOffer.jobType[jobTypesLanguage] as string}
                    </Typography>
                </Box>

                <Typography
                    variant="caption"
                    variantMapping={{ caption: 'p' }}
                    align="center"
                    style={{ marginTop: '1rem' }}
                    data-target="job-offer-card-text"
                    data-target-id="job-offer-card-date"
                    noWrap
                >
                    {t('company:viewJobOffers.publishedSince', {
                        dateSince: formatDistanceToNow(jobOffer.updatedAt as string, i18n.language as SupportedLangs),
                    })}
                </Typography>
            </CardContent>
            <CardActions>
                <Button
                    data-target="job-offer-card-button"
                    data-target-id="job-offer-card-view"
                    variant="contained"
                    sx={paddedButton}
                    onClick={() => router.push(`${routePathCompany}/${jobOffer.company.id}/job/${jobOffer.id}`)}
                    fullWidth
                >
                    {t('company:viewJobOffers.view')}
                </Button>
            </CardActions>
        </StyledCard>
    );
};

export default JobOfferCard;
